import { useState, useEffect } from "react";
import useData from "./useData";
import usePage from "./usePage";

const useExpense = () => {
  const { executeAction, getFromServer } = useData();
  const { showMessage } = usePage();

  const [expenses, setExpenses] = useState([]);
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get expenses for a specific user and pay period
  const getByUserAndPeriod = async (userId, payPeriodId) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "Expense",
        action: "getByUserAndPeriod",
        data: { userId, payPeriodId },
      });

      if (results?.isSuccessful) {
        setExpenses(results.data || []);
        return results.data;
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Get all pending expense approvals
  const getPendingApprovals = async () => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "Expense",
        action: "getPendingApprovals",
        data: {},
      });

      if (results?.isSuccessful) {
        setPendingExpenses(results.data || []);
        return results.data;
      }
    } catch (err) {
      showMessage(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getPendingApprovalsDashboard = async ({ supervisorId, projectId }) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "Expense",
        action: "getPendingApprovalsDashboard",
        data: {
          supervisorId: supervisorId || null,
          projectId: projectId || null,
        },
      });

      if (results?.isSuccessful) {
        setPendingExpenses(results.data || []);
        return results.data;
      }
      return [];
    } catch (err) {
      showMessage(err.message, "error");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Approve an expense
  const approveExpense = async (
    expenseId,
    approvedBy,
    notes,
    editedFields = {},
  ) => {
    try {
      const results = await executeAction({
        entityName: "Expense",
        action: "approve",
        data: { id: expenseId, approvedBy, notes, ...editedFields },
      });

      if (results?.isSuccessful) {
        showMessage("Expense approved", "success");
        return results.data;
      } else {
        showMessage(results?.message || "Failed to approve expense", "error");
        return null;
      }
    } catch (err) {
      console.error("[useExpense.approveExpense] Error:", err);
      showMessage(err.message, "error");
      return null;
    }
  };

  // Reject an expense
  const rejectExpense = async (
    expenseId,
    approvedBy,
    rejectionReason,
    editedFields = {},
  ) => {
    try {
      const results = await executeAction({
        entityName: "Expense",
        action: "reject",
        data: { id: expenseId, approvedBy, rejectionReason, ...editedFields },
      });

      if (results?.isSuccessful) {
        showMessage("Expense rejected", "success");
        return results.data;
      } else {
        showMessage(results?.message || "Failed to reject expense", "error");
        return null;
      }
    } catch (err) {
      console.error("[useExpense.rejectExpense] Error:", err);
      showMessage(err.message, "error");
      return null;
    }
  };

  // Get expense summary by type
  const getSummaryByType = async (payPeriodId, status = null) => {
    try {
      const results = await executeAction({
        entityName: "Expense",
        action: "getSummaryByType",
        data: { payPeriodId, status },
      });

      if (results?.isSuccessful) {
        return results.data;
      }
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  // Create new expense
  const createExpense = async (expenseData) => {
    try {
      const results = await executeAction({
        entityName: "Expense",
        action: "create",
        data: expenseData,
      });

      if (results?.isSuccessful) {
        showMessage("Expense created", "success");
        return results.data;
      } else {
        showMessage(results?.message || "Failed to create expense", "error");
      }
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  // Update expense
  const updateExpense = async (expenseData) => {
    try {
      const results = await executeAction({
        entityName: "Expense",
        action: "update",
        data: expenseData,
      });

      if (results?.isSuccessful) {
        showMessage("Expense updated", "success");
        return results.data;
      } else {
        showMessage(results?.message || "Failed to update expense", "error");
      }
    } catch (err) {
      showMessage(err.message, "error");
    }
  };

  // Get expense by ID
  const getById = async (expenseId) => {
    try {
      const results = await executeAction({
        entityName: "Expense",
        action: "getById",
        data: { id: expenseId },
      });

      if (results?.isSuccessful) {
        return results.data;
      } else {
        showMessage(results?.message || "Failed to load expense", "error");
        return null;
      }
    } catch (err) {
      showMessage(err.message, "error");
      return null;
    }
  };

  return {
    expenses,
    pendingExpenses,
    loading,
    getByUserAndPeriod,
    getPendingApprovals,
    getPendingApprovalsDashboard,
    approveExpense,
    rejectExpense,
    getSummaryByType,
    createExpense,
    updateExpense,
    getById,
  };
};

export default useExpense;
